import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaChevronDown,
  FaEnvelope,
  FaBars,
  FaTimes,
  FaArrowRight,
  FaShieldAlt,
  FaFileInvoiceDollar,
  FaWhatsapp,
  FaHeadset,
} from "react-icons/fa";
import { Link } from "react-router-dom";

/* =========================================================
   BRAND
========================================================= */

const BRAND_BLUE = "#2563EB";
const BRAND_PURPLE = "#4F46E5";
const DARK = "#0B1220";
const TEXT = "#172033";
const MUTED = "#64748B";

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 27px;

  flex: 1;

  @media (max-width: 1100px) {
    gap: 17px;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: #252b3a;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  transition: color 0.2s ease;

  &:hover {
    color: #150080;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  flex-shrink: 0;
`;

const LoginLink = styled(Link)`
  color: #202635;
  text-decoration: none;

  font-size: 14px;
  font-weight: 700;

  padding: 10px 12px;

  &:hover {
    color: #150080;
  }
`;

const StartButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  color: #ffffff;
  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );

  text-decoration: none;

  padding: 11px 18px;
  border-radius: 6px;

  font-size: 13px;
  font-weight: 700;

  transition:
    background 0.2s ease,
    transform 0.2s ease;

  span {
    font-size: 17px;
    line-height: 1;
  }

  &:hover {
    background: #10005e;
    transform: translateY(-1px);
  }
`;

const LoginButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  color: #ffffff;

  background: linear-gradient(
    135deg,
    #59047d 0%,
    #340548 55%,
    #59047d 100%
  );

  text-decoration: none;

  padding: 11px 18px;
  border-radius: 6px;

  font-size: 13px;
  font-weight: 700;

  box-shadow: 0 7px 18px rgba(21, 0, 128, 0.2);

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    filter 0.2s ease;

  span {
    font-size: 17px;
    line-height: 1;
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.08);
    box-shadow: 0 10px 24px rgba(21, 0, 128, 0.28);
  }
`;

const Header = styled.header`
  width: 100%;
  height: 76px;
  background: #ffffff;
  border-bottom: 1px solid #e8e8ef;
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: 1380px;
  height: 100%;
  margin: 0 auto;
  padding: 0 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;

  @media (max-width: 900px) {
    padding: 0 24px;
  }
`;

/* =========================================================
   GLOBAL
========================================================= */

const Page = styled.div`
  min-height: 100vh;

  background:
    radial-gradient(
      circle at 80% 0%,
      rgba(79, 70, 229, 0.09),
      transparent 32%
    ),
    radial-gradient(
      circle at 10% 25%,
      rgba(37, 99, 235, 0.07),
      transparent 28%
    ),
    #f8fafc;

  color: ${TEXT};
`;

/* =========================================================
   NAVBAR
========================================================= */

const TopNav = styled.header`
  position: sticky;
  top: 0;
  z-index: 1000;

  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(16px);

  border-bottom: 1px solid #e5e7eb;
`;

const NavInner = styled.div`
  max-width: 1180px;
  height: 74px;

  margin: 0 auto;
  padding: 0 24px;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;

  text-decoration: none;
  color: ${DARK};
`;

const BrandMark = styled.div`
  width: 38px;
  height: 38px;

  border-radius: 10px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );

  color: white;

  font-size: 13px;
  font-weight: 800;

  box-shadow: 0 7px 18px rgba(37, 99, 235, 0.2);
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;

  line-height: 1;

  strong {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.4px;
    color: ${DARK};
  }

  span {
    margin-top: 4px;

    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.6px;

    color: ${BRAND_BLUE};
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 30px;

  a {
    position: relative;

    color: #475569;
    text-decoration: none;

    font-size: 14px;
    font-weight: 600;

    transition: 0.2s ease;

    &:hover {
      color: ${BRAND_BLUE};
    }
  }

  .active {
    color: ${BRAND_BLUE};
  }

  .active::after {
    content: "";

    position: absolute;

    left: 0;
    right: 0;
    bottom: -27px;

    height: 2px;

    border-radius: 2px;

    background: ${BRAND_BLUE};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 7px;

  padding: 10px 17px;

  border-radius: 8px;

  color: white !important;
  text-decoration: none;

  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );

  font-size: 13px !important;
  font-weight: 700 !important;

  box-shadow: 0 7px 16px rgba(37, 99, 235, 0.18);

  transition: 0.2s ease;

  &:hover {
    color: white !important;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Burger = styled.button`
  display: none;

  border: 0;
  background: transparent;

  color: ${DARK};

  font-size: 21px;

  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

/* =========================================================
   MOBILE MENU
========================================================= */

const MobileMenu = styled(motion.div)`
  position: fixed;
  inset: 0;

  z-index: 2000;

  background: white;

  padding: 90px 28px 30px;
`;

const Close = styled.button`
  position: absolute;

  top: 24px;
  right: 24px;

  border: 0;
  background: transparent;

  color: ${DARK};

  font-size: 23px;

  cursor: pointer;
`;

const MobileLinks = styled.div`
  display: flex;
  flex-direction: column;

  gap: 25px;

  a {
    color: ${DARK};
    text-decoration: none;

    font-size: 22px;
    font-weight: 700;
  }
`;

const MobileCta = styled(Link)`
  margin-top: 10px;

  display: inline-flex;

  justify-content: center;
  align-items: center;

  gap: 8px;

  padding: 13px 18px;

  border-radius: 9px;

  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );

  color: white !important;

  font-size: 15px !important;
`;

/* =========================================================
   HERO
========================================================= */

const Hero = styled.section`
  max-width: 1180px;

  margin: 0 auto;

  padding: 78px 24px 48px;

  display: grid;

  grid-template-columns: 1.25fr 0.75fr;

  align-items: center;

  gap: 70px;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;

    padding-top: 58px;

    gap: 35px;
  }
`;

const HeroContent = styled.div`
  max-width: 700px;
`;

const Eyebrow = styled.div`
  display: inline-flex;

  align-items: center;

  gap: 8px;

  padding: 7px 12px;

  border-radius: 999px;

  background: #eff6ff;

  border: 1px solid #dbeafe;

  color: ${BRAND_BLUE};

  font-size: 11px;

  font-weight: 800;

  letter-spacing: 0.8px;

  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  margin: 18px 0 16px;

  color: ${DARK};

  font-size: clamp(38px, 5vw, 58px);

  line-height: 1.06;

  letter-spacing: -2.4px;

  font-weight: 800;

  span {
    background: linear-gradient(
      135deg,
      ${BRAND_BLUE},
      ${BRAND_PURPLE}
    );

    -webkit-background-clip: text;

    -webkit-text-fill-color: transparent;
  }
`;

const HeroText = styled.p`
  max-width: 650px;

  margin: 0;

  color: ${MUTED};

  font-size: 17px;

  line-height: 1.75;
`;

const HeroNote = styled.div`
  display: flex;

  align-items: center;

  gap: 9px;

  margin-top: 24px;

  color: #64748b;

  font-size: 12px;

  svg {
    color: ${BRAND_BLUE};
  }
`;

/* =========================================================
   HERO VISUAL
========================================================= */

const HeroVisual = styled.div`
  position: relative;

  min-height: 280px;

  display: flex;

  align-items: center;

  justify-content: center;
`;

const Glow = styled.div`
  position: absolute;

  width: 245px;
  height: 245px;

  border-radius: 50%;

  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.17),
    rgba(79, 70, 229, 0.12)
  );

  filter: blur(4px);
`;

const VisualCard = styled(motion.div)`
  position: relative;

  z-index: 2;

  width: 100%;

  max-width: 350px;

  padding: 27px;

  border: 1px solid #e2e8f0;

  border-radius: 18px;

  background: rgba(255, 255, 255, 0.92);

  box-shadow:
    0 24px 60px rgba(15, 23, 42, 0.1),
    0 3px 12px rgba(15, 23, 42, 0.04);
`;

const VisualHeader = styled.div`
  display: flex;

  align-items: center;

  gap: 13px;

  padding-bottom: 20px;

  border-bottom: 1px solid #edf2f7;
`;

const VisualIcon = styled.div`
  width: 44px;
  height: 44px;

  border-radius: 12px;

  display: flex;

  align-items: center;
  justify-content: center;

  color: white;

  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );
`;

const VisualTitle = styled.div`
  strong {
    display: block;

    color: ${DARK};

    font-size: 15px;
  }

  span {
    display: block;

    margin-top: 4px;

    color: #94a3b8;

    font-size: 11px;
  }
`;

const VisualLine = styled.div`
  display: flex;

  align-items: center;

  gap: 9px;

  margin-top: 17px;

  color: #475569;

  font-family: monospace;

  font-size: 11px;

  span {
    color: ${BRAND_BLUE};

    font-weight: 700;
  }
`;

/* =========================================================
   POLICY SECTION
========================================================= */

const PolicySection = styled.section`
  max-width: 1180px;

  margin: 0 auto;

  padding: 22px 24px 85px;
`;

const SectionHeader = styled.div`
  margin-bottom: 25px;

  h2 {
    margin: 0;

    color: ${DARK};

    font-size: 27px;

    letter-spacing: -0.7px;
  }

  p {
    margin: 7px 0 0;

    color: ${MUTED};

    font-size: 14px;

    line-height: 1.6;
  }
`;

/* =========================================================
   POLICY GRID
========================================================= */

const PolicyGrid = styled.div`
  display: grid;

  grid-template-columns: repeat(2, minmax(0, 1fr));

  gap: 17px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

/* =========================================================
   POLICY CARD
========================================================= */

const PolicyCard = styled(motion.div)`
  border: 1px solid #e2e8f0;

  border-radius: 13px;

  background: white;

  overflow: hidden;

  box-shadow: 0 7px 24px rgba(15, 23, 42, 0.045);

  transition: 0.22s ease;

  &:hover {
    border-color: #c7d2fe;

    box-shadow: 0 14px 32px rgba(37, 99, 235, 0.08);
  }
`;

const AccordionHeader = styled.button`
  width: 100%;

  padding: 19px 20px;

  display: flex;

  align-items: center;

  gap: 13px;

  border: 0;

  background: white;

  color: ${DARK};

  text-align: left;

  cursor: pointer;
`;

const Number = styled.div`
  width: 37px;
  height: 37px;

  flex-shrink: 0;

  display: flex;

  align-items: center;
  justify-content: center;

  border-radius: 9px;

  background: #eff6ff;

  color: ${BRAND_BLUE};

  font-size: 11px;

  font-weight: 800;
`;

const HeaderText = styled.div`
  flex: 1;

  strong {
    display: block;

    color: ${DARK};

    font-size: 14px;

    font-weight: 750;
  }

  span {
    display: block;

    margin-top: 4px;

    color: #94a3b8;

    font-size: 10px;

    font-weight: 600;

    text-transform: uppercase;

    letter-spacing: 0.7px;
  }
`;

const Chevron = styled(motion.div)`
  width: 32px;
  height: 32px;

  flex-shrink: 0;

  display: flex;

  align-items: center;
  justify-content: center;

  border-radius: 8px;

  background: #f8fafc;

  color: ${BRAND_BLUE};

  font-size: 12px;
`;

const AccordionContent = styled(motion.div)`
  overflow: hidden;
`;

const ContentInner = styled.div`
  padding: 0 20px 20px 70px;

  color: ${MUTED};

  font-size: 13.5px;

  line-height: 1.75;

  @media (max-width: 600px) {
    padding-left: 20px;
  }
`;

const PolicyText = styled.p`
  margin: 0 0 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Highlight = styled.span`
  color: ${BRAND_BLUE};

  font-weight: 700;
`;

/* =========================================================
   SUPPORT BANNER
========================================================= */

const SupportBanner = styled(motion.div)`
  margin-top: 22px;

  padding: 26px 28px;

  border-radius: 15px;

  background: linear-gradient(
    135deg,
    #0f172a,
    #172554
  );

  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 25px;

  color: white;

  @media (max-width: 700px) {
    flex-direction: column;

    align-items: flex-start;
  }
`;

const SupportContent = styled.div`
  display: flex;

  align-items: center;

  gap: 15px;
`;

const SupportIcon = styled.div`
  width: 43px;
  height: 43px;

  flex-shrink: 0;

  display: flex;

  align-items: center;
  justify-content: center;

  border-radius: 10px;

  background: rgba(255, 255, 255, 0.1);

  color: #93c5fd;
`;

const SupportText = styled.div`
  strong {
    display: block;

    font-size: 15px;
  }

  span {
    display: block;

    margin-top: 5px;

    color: #cbd5e1;

    font-size: 12px;

    line-height: 1.5;
  }

  a {
    display: inline-block;

    margin-top: 5px;

    color: #93c5fd;

    text-decoration: none;

    font-size: 12px;

    font-weight: 700;
  }
`;

const ContactButton = styled(Link)`
  display: inline-flex;

  align-items: center;

  gap: 8px;

  padding: 10px 15px;

  border-radius: 8px;

  background: white;

  color: ${DARK};

  text-decoration: none;

  font-size: 12px;

  font-weight: 700;

  white-space: nowrap;

  transition: 0.2s ease;

  &:hover {
    transform: translateY(-1px);

    color: ${BRAND_BLUE};
  }
`;

/* =========================================================
   FOOTER
========================================================= */

const Footer = styled.footer`
  border-top: 1px solid #e2e8f0;

  background: white;
`;

const FooterInner = styled.div`
  max-width: 1180px;

  margin: auto;

  padding: 25px 24px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 20px;

  @media (max-width: 650px) {
    flex-direction: column;

    align-items: flex-start;
  }
`;

const FooterBrand = styled.div`
  color: #64748b;

  font-size: 12px;

  strong {
    color: ${DARK};
  }
`;

const FooterLinks = styled.div`
  display: flex;

  gap: 20px;

  a {
    color: #64748b;

    text-decoration: none;

    font-size: 12px;

    font-weight: 600;

    &:hover {
      color: ${BRAND_BLUE};
    }
  }
`;

/* =========================================================
   WHATSAPP
========================================================= */

const WhatsAppButton = styled.a`
  position: fixed;

  right: 22px;
  bottom: 22px;

  width: 52px;
  height: 52px;

  display: flex;

  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: #25d366;

  color: white;

  font-size: 23px;

  text-decoration: none;

  z-index: 900;

  box-shadow: 0 10px 25px rgba(37, 211, 102, 0.25);

  transition: 0.2s ease;

  &:hover {
    transform: translateY(-3px) scale(1.04);
  }

  @media (max-width: 600px) {
    right: 16px;
    bottom: 16px;
  }
`;

/* =========================================================
   ACCORDION ITEM
========================================================= */

const AccordionItem = ({ number, title, children }) => {
  const [open, setOpen] = useState(false);

  return (
    <PolicyCard
      initial={{
        opacity: 0,
        y: 15,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      viewport={{
        once: true,
        amount: 0.15,
      }}
      transition={{
        duration: 0.35,
      }}
    >
      <AccordionHeader
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <Number>
          {String(number).padStart(2, "0")}
        </Number>

        <HeaderText>
          <strong>{title}</strong>
          <span>Refund policy</span>
        </HeaderText>

        <Chevron
          animate={{
            rotate: open ? 180 : 0,
          }}
          transition={{
            duration: 0.22,
          }}
        >
          <FaChevronDown />
        </Chevron>
      </AccordionHeader>

      <AnimatePresence initial={false}>
        {open && (
          <AccordionContent
            initial={{
              height: 0,
              opacity: 0,
            }}
            animate={{
              height: "auto",
              opacity: 1,
            }}
            exit={{
              height: 0,
              opacity: 0,
            }}
            transition={{
              duration: 0.28,
              ease: "easeOut",
            }}
          >
            <ContentInner>
              {children}
            </ContentInner>
          </AccordionContent>
        )}
      </AnimatePresence>
    </PolicyCard>
  );
};

/* =========================================================
   PAGE
========================================================= */

const RefundPolicy = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <Page>
      {/* =====================================================
          NAVIGATION
      ===================================================== */}
      <Header>
        <HeaderInner>
          {/* <LogoWrap>
            <Logo
              src={logo}
              alt="Recharge Junction Logo"
              onError={(e) => {
                e.currentTarget.src = defaultLogo;
              }}
            />
          </LogoWrap> */}
          <Brand to="/">
            <BrandMark>API</BrandMark>

            <BrandText>
              <strong>API EXCHANGE</strong>
              <span>POWERING BUSINESS</span>
            </BrandText>
          </Brand>

          <Navigation>
            {/* <NavLink to="/services">Services</NavLink> */}
            <NavLink to="/privacy-policy">Privacy Policy</NavLink>
            <NavLink to="/terms-and-conditions">
              Terms & Conditions
            </NavLink>
            <NavLink to="/refund-policy">
              Refund & Cancellation
            </NavLink>
            <NavLink to="/contact">Contact Us</NavLink>
          </Navigation>

          <HeaderActions>
            <LoginButton to="/login">
              Login
              <span>→</span>
            </LoginButton>

            <StartButton to="/register">
              Start Free
              <span>→</span>
            </StartButton>
          </HeaderActions>
        </HeaderInner>
      </Header>

      {/* =====================================================
          MOBILE MENU
      ===================================================== */}

      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            initial={{
              opacity: 0,
              x: "100%",
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            exit={{
              opacity: 0,
              x: "100%",
            }}
            transition={{
              duration: 0.22,
            }}
          >
            <Close
              type="button"
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
            >
              <FaTimes />
            </Close>

            <MobileLinks>
              <Link
                to="/"
                onClick={() => setMenuOpen(false)}
              >
                Home
              </Link>

              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
              >
                Login
              </Link>

              <Link
                to="/register"
                onClick={() => setMenuOpen(false)}
              >
                Register
              </Link>

              <Link
                to="/contact"
                onClick={() => setMenuOpen(false)}
              >
                Contact
              </Link>

              <MobileCta
                to="/register"
                onClick={() => setMenuOpen(false)}
              >
                Start Free
                <FaArrowRight />
              </MobileCta>
            </MobileLinks>
          </MobileMenu>
        )}
      </AnimatePresence>

      {/* =====================================================
          HERO
      ===================================================== */}

      <Hero>
        <HeroContent>
          <Eyebrow>
            <FaShieldAlt />
            Refund & Cancellation
          </Eyebrow>

          <HeroTitle>
            Clear terms.
            <br />
            <span>Transparent process.</span>
          </HeroTitle>

          <HeroText>
            Please review our refund and cancellation terms
            carefully before using our services. This policy
            explains how refund requests, processing timelines
            and transaction-related disputes are handled.
          </HeroText>

          <HeroNote>
            <FaFileInvoiceDollar />
            Please retain your transaction ID for any refund
            related enquiry.
          </HeroNote>
        </HeroContent>

        <HeroVisual>
          <Glow />

          <VisualCard
            initial={{
              opacity: 0,
              y: 15,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.5,
            }}
          >
            <VisualHeader>
              <VisualIcon>
                <FaShieldAlt />
              </VisualIcon>

              <VisualTitle>
                <strong>Refund Policy</strong>

                <span>
                  API Exchange · Service Terms
                </span>
              </VisualTitle>
            </VisualHeader>

            <VisualLine>
              <span>STATUS</span>
              Policy Active
            </VisualLine>

            <VisualLine>
              <span>REQUEST</span>
              Transaction Review
            </VisualLine>

            <VisualLine>
              <span>SUPPORT</span>
              Available
            </VisualLine>
          </VisualCard>
        </HeroVisual>
      </Hero>

      {/* =====================================================
          POLICY CONTENT
      ===================================================== */}

      <PolicySection>
        <SectionHeader>
          <h2>Refund & cancellation terms</h2>

          <p>
            The following sections explain eligibility,
            refund requests, processing timelines and
            transaction disputes.
          </p>
        </SectionHeader>

        <PolicyGrid>
          {/* STEP 1 */}

          <AccordionItem
            number={1}
            title="Eligibility for Refunds"
          >
            <PolicyText>
              Refunds are only applicable to transactions where
              a valid error or failure is attributable to our
              services. We do not guarantee refunds for issues
              caused by user error, third-party services, or
              force majeure events.
            </PolicyText>
          </AccordionItem>

          {/* STEP 2 */}

          <AccordionItem
            number={2}
            title="Non-Refundable Items"
          >
            <PolicyText>
              All digital services, subscriptions, and
              processing fees are non-refundable once delivered
              or processed, except where required by law or
              explicitly stated otherwise.
            </PolicyText>
          </AccordionItem>

          {/* STEP 3 */}

          <AccordionItem
            number={3}
            title="Requesting a Refund"
          >
            <PolicyText>
              To request a refund, you must contact our support
              team within{" "}
              <Highlight>3 business days</Highlight>{" "}
              of the transaction.
            </PolicyText>

            <PolicyText>
              Requests must include your{" "}
              <Highlight>transaction ID</Highlight>, the date
              of payment, and a detailed explanation of the
              issue.
            </PolicyText>
          </AccordionItem>

          {/* STEP 4 */}

          <AccordionItem
            number={4}
            title="Processing Time"
          >
            <PolicyText>
              Approved refunds will be processed within{" "}
              <Highlight>7 to 14 business days</Highlight>.
            </PolicyText>

            <PolicyText>
              The credited amount will be refunded to the
              original payment method used during the
              transaction.
            </PolicyText>
          </AccordionItem>

          {/* STEP 5 */}

          <AccordionItem
            number={5}
            title="Partial Refunds"
          >
            <PolicyText>
              In cases where only part of the service was not
              delivered as expected, a partial refund may be
              issued at our discretion.
            </PolicyText>
          </AccordionItem>

          {/* STEP 6 */}

          <AccordionItem
            number={6}
            title="Chargebacks"
          >
            <PolicyText>
              If you initiate a chargeback without first
              contacting our support team, you may be
              permanently suspended from using our services.
            </PolicyText>

            <PolicyText>
              We reserve the right to dispute any chargebacks
              with relevant evidence.
            </PolicyText>
          </AccordionItem>

          {/* STEP 7 */}

          <AccordionItem
            number={7}
            title="Changes to Refund Policy"
          >
            <PolicyText>
              We reserve the right to modify or update this
              Refund Policy at any time without prior notice.
            </PolicyText>

            <PolicyText>
              Continued use of the website or services
              constitutes your acceptance of any changes.
            </PolicyText>
          </AccordionItem>

          {/* STEP 8 */}

          <AccordionItem
            number={8}
            title="Need Assistance?"
          >
            <PolicyText>
              For refund or cancellation queries, please
              contact our support team at{" "}
              <Highlight>
                help.apiexchange.co@gmail.com
              </Highlight>
              .
            </PolicyText>
          </AccordionItem>
        </PolicyGrid>

        {/* ===================================================
            SUPPORT BANNER
        =================================================== */}

        <SupportBanner
          initial={{
            opacity: 0,
            y: 15,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.4,
          }}
        >
          <SupportContent>
            <SupportIcon>
              <FaHeadset />
            </SupportIcon>

            <SupportText>
              <strong>Need help with a refund?</strong>

              <span>
                Contact our support team with your transaction
                details and we’ll help you with your query.
              </span>

              <a href="mailto:help.apiexchange.co@gmail.com">
                help.apiexchange.co@gmail.com
              </a>
            </SupportText>
          </SupportContent>

          <ContactButton to="/contact">
            Contact Support
            <FaArrowRight />
          </ContactButton>
        </SupportBanner>
      </PolicySection>

      {/* =====================================================
          FOOTER
      ===================================================== */}

      {/* <Footer>
        <FooterInner>
          <FooterBrand>
            © {new Date().getFullYear()}{" "}
            <strong>API Exchange</strong>. All rights reserved.
          </FooterBrand>

          <FooterLinks>
            <Link to="/">Home</Link>

            <Link to="/login">Login</Link>

            <Link to="/register">Register</Link>

            <Link to="/contact">Contact</Link>
          </FooterLinks>
        </FooterInner>
      </Footer> */}

      {/* =====================================================
          FLOATING WHATSAPP
      ===================================================== */}

      <WhatsAppButton
        href="https://wa.me/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </WhatsAppButton>
    </Page>
  );
};

export default RefundPolicy;